import React, { FocusEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ReferenceField } from '@1po/1po-bff-fe-spec/generated/estimate/request/UpdateReference';
import { MyStoreReference } from '@1po/1po-bff-fe-spec/generated/estimate/response/AutocompleteSearchReferencesResponse';
import { useTheme } from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { TrashAltIcon } from 'assets/icons';
import {
  addReferenceFromAutocomplete,
  getFreeReferenceSubsectionSearch,
  sendAutocompleteReferencesRequest,
} from 'domains/estimate/Estimate.store';
import { ReferenceSearchParams } from 'domains/estimate/Estimate.types';
import ReferenceAutocompleteMenu from 'pages/EstimatePage/TableSection/ReferenceAutocompleteMenu';
import { isEmpty } from 'pages/EstimatePage/TableSection/tableComponents';
import { TooltipContent } from 'pages/EstimatePage/TableSection/TableSection.styled';
import { AnchoredPortal, Flex, Icon, MarginBox, WithTooltip } from 'UI';
import { SearchContainer, SSearchInput } from './Autocomplete.styled';

const ReferenceAutocomplete = ({
  value,
  saveFunction,
  rowId,
  fieldId,
  removeRow,
  readOnly = false,
  isInvalid = false,
  minWidth,
  'data-cy': dataCy,
}: {
  value: string;
  saveFunction: (value: string) => void;
  rowId: string;
  fieldId: ReferenceField;
  removeRow?: () => void;
  readOnly?: boolean;
  isInvalid?: boolean;
  minWidth?: number;
  'data-cy'?: string;
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesignationField = fieldId === 'NAME';

  const [referenceSearch, setReferenceSearch] = useState<ReferenceSearchParams>({
    queryReference: undefined,
    queryDescription: undefined,
    pending: false,
    isValid: false,
    open: false,
    focusMenu: undefined,
    searchId: undefined,
  });

  const referenceSearchRef = useRef(referenceSearch);
  referenceSearchRef.current = referenceSearch;

  const isFocused = useRef(false);
  const lastSentQuery = useRef<{ queryReference: string; queryDescription: string } | null>(null);
  const selectionMade = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [localResults, setLocalResults] = useState<MyStoreReference[]>([]);
  const [referenceInputValue, setReferenceInputValue] = useState(value);

  useEffect(() => {
    if (!isFocused.current) {
      setReferenceInputValue(value);
    }
  }, [value]);

  const storeSearchState = useSelector(getFreeReferenceSubsectionSearch);

  useEffect(() => {
    if (!Array.isArray(storeSearchState?.fulltextAutocomplete)) {
      return;
    }
    if (selectionMade.current) {
      selectionMade.current = false;
      return;
    }
    const last = lastSentQuery.current;
    if (!last) {
      return;
    }
    const storeKeyCode = (storeSearchState.lastAutocompleteKeyCode ?? '').toLowerCase();
    const storeKeyDesc = (storeSearchState.lastAutocompleteKeyDescription ?? '').toLowerCase();
    const queryRef = last.queryReference.toLowerCase();
    const queryDesc = last.queryDescription.toLowerCase();

    const refMatches = fieldId === 'REFERENCE_NUMBER' && queryRef.length > 0 && storeKeyCode === queryRef;
    const descMatches = fieldId === 'NAME' && queryDesc.length > 0 && storeKeyDesc === queryDesc;

    if (refMatches || descMatches) {
      setLocalResults(storeSearchState.fulltextAutocomplete);
      if (isFocused.current) {
        setReferenceSearch((prev) => ({ ...prev, open: true, pending: false }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeSearchState, fieldId]);

  useEffect(() => {
    const current = referenceSearchRef.current;
    // when selected, do not send request to update the menu
    if (selectionMade.current) {
      return;
    }
    if (current && !current.pending && (current.queryReference || current.queryDescription) && current.isValid) {
      setLocalResults([]);
      lastSentQuery.current = {
        queryReference: current.queryReference ?? '',
        queryDescription: current.queryDescription ?? '',
      };
      dispatch(
        sendAutocompleteReferencesRequest({
          queryReference: current.queryReference ?? '',
          queryDescription: current.queryDescription ?? '',
        }),
      );
      setReferenceSearch((prev) => ({ ...prev, pending: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, referenceSearch.queryReference, referenceSearch.queryDescription, referenceSearch.isValid]);

  const SearchInputID = `search-reference-${fieldId}-fulltext-id`;
  const SearchInputName = `search-reference-${fieldId}-fulltext-name`;

  const onCodeDebouncedChange = useCallback((val: string) => {
    if (val?.length > 2) {
      setReferenceSearch({
        ...referenceSearchRef.current,
        queryReference: val.toLowerCase(),
        queryDescription: undefined,
        pending: false,
        isValid: true,
        open: true,
        focusMenu: undefined,
      });
    } else {
      setLocalResults([]);
      lastSentQuery.current = null;
      setReferenceSearch({
        queryReference: undefined,
        queryDescription: undefined,
        pending: false,
        isValid: false,
        open: false,
        focusMenu: undefined,
        searchId: undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDescriptionDebouncedChange = useCallback((val: string) => {
    if (val?.length > 2) {
      setReferenceSearch({
        ...referenceSearchRef.current,
        queryReference: undefined,
        queryDescription: val.toLowerCase(),
        pending: false,
        isValid: true,
        open: true,
        focusMenu: undefined,
      });
    } else {
      setLocalResults([]);
      lastSentQuery.current = null;
      setReferenceSearch({
        queryReference: undefined,
        queryDescription: undefined,
        pending: false,
        isValid: false,
        open: false,
        focusMenu: undefined,
        searchId: undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickSelection = useCallback(
    (label: string, id: string, code: string, clientPriceVatExcluded: string, garagePriceVatExcluded: string) => () => {
      const input = fieldId === 'REFERENCE_NUMBER' ? code : label;
      if (!input || !id) {
        return;
      }
      const searchId = uuidv4();
      setReferenceInputValue(input);
      setLocalResults([]);
      lastSentQuery.current = null;
      selectionMade.current = true;
      setReferenceSearch({
        queryReference: fieldId === 'REFERENCE_NUMBER' ? code : '',
        queryDescription: fieldId === 'NAME' ? label : '',
        pending: false,
        isValid: true,
        open: false,
        focusMenu: undefined,
        searchId,
      });
      const reference = {
        referenceId: id,
        referenceNumber: code,
        designation: label,
        clientPriceVatExcluded,
        garagePriceVatExcluded,
      };
      dispatch(addReferenceFromAutocomplete({ reference, itemId: rowId }));
    },
    [dispatch, rowId, fieldId],
  );

  const handleQueryInputValue =
    fieldId === 'NAME'
      ? handleClickSelection(referenceInputValue.trim(), '', '', '', '')
      : handleClickSelection('', '', referenceInputValue.trim(), '', '');

  const resultsLength = localResults.length;

  const handleMove = useCallback(
    (offset: number) => {
      const current = referenceSearchRef.current;
      if (current.open) {
        setReferenceSearch({
          ...current,
          focusMenu: current?.focusMenu ? (current.focusMenu + offset) % resultsLength : 0,
        });
      }
    },
    [resultsLength],
  );

  const handleDown = useCallback(() => handleMove(1), [handleMove]);
  const handleUp = useCallback(() => handleMove(-1), [handleMove]);

  const handleKeyPress = useCallback(
    (event: { key: string }) => {
      if (event.key === 'Escape') {
        setReferenceInputValue(value);
        setLocalResults([]);
        lastSentQuery.current = null;
        setReferenceSearch({
          queryReference: undefined,
          queryDescription: undefined,
          pending: false,
          isValid: false,
          open: false,
          focusMenu: undefined,
          searchId: undefined,
        });
      }
      if (event.key === 'ArrowUp') {
        handleUp();
      }
      if (event.key === 'ArrowDown') {
        handleDown();
      }
    },
    [handleDown, handleUp, value],
  );

  const handleBlurInput = (_val: string, e?: FocusEvent) => {
    isFocused.current = false;
    const current = referenceSearchRef.current;
    if (current?.focusMenu !== undefined) {
      return;
    }
    if (e?.relatedTarget?.id.includes('search-menu')) {
      return;
    }
    lastSentQuery.current = null;
    setLocalResults([]);
    setReferenceSearch({
      queryReference: undefined,
      queryDescription: undefined,
      pending: false,
      isValid: false,
      open: false,
      focusMenu: undefined,
      searchId: undefined,
    });
    saveFunction(_val);
  };

  const change = () => {
    return fieldId === 'REFERENCE_NUMBER' ? onCodeDebouncedChange : onDescriptionDebouncedChange;
  };

  const getPlaceholderText = () => {
    if (isDesignationField) {
      return t('estimate.references.search_references_designation', 'Search designation');
    }
    return t('estimate.references.search_references', 'Search references');
  };

  return (
    <SearchContainer ref={containerRef}>
      <Flex justify={'center'} align={'center'} minWidth={minWidth}>
        {removeRow && (
          <>
            <Icon
              IconComponent={TrashAltIcon}
              onClick={() => removeRow()}
              size={20}
              color={theme.color.brand_black}
              data-cy={'remove-row'}
            />
            <MarginBox ml={15} />
          </>
        )}
        <WithTooltip title={readOnly ? referenceInputValue : undefined} placement={'top'}>
          <TooltipContent>
            <SSearchInput
              $isInvalid={isInvalid || (isEmpty(value) && fieldId === 'NAME')}
              id={SearchInputID}
              name={SearchInputName}
              autoComplete={`on section-${SearchInputName}`}
              value={referenceInputValue}
              onChange={(val) => !readOnly && setReferenceInputValue(val)}
              onChangeDebounced={readOnly ? undefined : change()}
              onPressEnter={readOnly ? undefined : handleQueryInputValue}
              onKeyPress={handleKeyPress}
              onFocus={() => {
                isFocused.current = true;
              }}
              onBlur={readOnly ? undefined : handleBlurInput}
              placeholder={getPlaceholderText()}
              readOnly={readOnly}
              data-cy={dataCy}
            />
          </TooltipContent>
        </WithTooltip>
      </Flex>
      {!readOnly && localResults.length > 0 && referenceSearch?.open && (
        <AnchoredPortal anchorRef={containerRef}>
          <ReferenceAutocompleteMenu
            results={localResults}
            search={referenceSearch}
            setSearch={setReferenceSearch}
            onClick={handleClickSelection}
            inputValue={referenceInputValue}
            field={fieldId}
          />
        </AnchoredPortal>
      )}
    </SearchContainer>
  );
};

export default ReferenceAutocomplete;
